import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangleIcon, LogOutIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useSignOut } from "@/hooks/auth/use-signout";
import { authClient, useSession } from "@/lib/auth-client";
import { noIndexSeo } from "@/lib/seo";
import * as m from "@/paraglide/messages";

export const Route = createFileRoute("/(app)/settings/danger")({
	component: RouteComponent,
	head: ({ match }) =>
		noIndexSeo({
			title: `${m.danger_title()} | StudyOS`,
			description: m.danger_description(),
			pathname: match.pathname,
		}),
});

function RouteComponent() {
	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium text-destructive flex items-center gap-2">
					<AlertTriangleIcon className="size-5" />
					{m.danger_title()}
				</h3>
				<p className="text-sm text-muted-foreground">{m.danger_description()}</p>
			</div>
			<Separator />
			<SignOutAllCard />
			<DeleteAccountCard />
		</div>
	);
}

function SignOutAllCard() {
	const { mutate: signOut, isPending } = useSignOut({ onlyCurrentSession: false });

	return (
		<Card className="border-border">
			<CardHeader>
				<CardTitle className="text-base flex items-center gap-2">
					<LogOutIcon className="size-4" />
					{m.danger_signOutAll()}
				</CardTitle>
				<CardDescription>{m.danger_signOutAllDesc()}</CardDescription>
			</CardHeader>
			<CardContent>
				<Button variant="outline" size="sm" disabled={isPending} onClick={() => signOut()}>
					{isPending ? m.danger_signingOut() : m.danger_signOutAll()}
				</Button>
			</CardContent>
		</Card>
	);
}

function DeleteAccountCard() {
	const { data: session } = useSession();
	const [open, setOpen] = useState(false);
	const [confirmEmail, setConfirmEmail] = useState("");

	const userEmail = session?.user?.email ?? "";

	const { mutate: deleteAccount, isPending } = useMutation({
		mutationKey: ["session", "delete-account"],
		mutationFn: async () => {
			const response = await authClient.deleteUser();

			if (response?.error) {
				throw response.error;
			}

			return response.data;
		},
		meta: {
			success: { message: m.danger_accountDeleted() },
			error: { message: m.danger_deleteAccountFailed() },
		},
		onSuccess: () => {
			window.location.href = "/sign-in";
		},
	});

	const isConfirmed = confirmEmail === userEmail;

	return (
		<Card className="border-destructive/50">
			<CardHeader>
				<CardTitle className="text-base text-destructive flex items-center gap-2">
					<Trash2Icon className="size-4" />
					{m.danger_deleteAccount()}
				</CardTitle>
				<CardDescription>{m.danger_deleteAccountDesc()}</CardDescription>
			</CardHeader>
			<CardContent>
				<Dialog
					open={open}
					onOpenChange={(v) => {
						setOpen(v);
						if (!v) setConfirmEmail("");
					}}
				>
					<DialogTrigger
						render={
							<Button variant="destructive" size="sm">
								{m.danger_deleteAccount()}
							</Button>
						}
					/>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>{m.danger_confirmDeleteTitle()}</DialogTitle>
							<DialogDescription>{m.danger_confirmDeleteDesc()}</DialogDescription>
						</DialogHeader>
						<div className="space-y-2">
							<Label>
								{m.danger_enterEmailPrefix()}{" "}
								<span className="font-mono text-destructive">{userEmail}</span>{" "}
								{m.danger_enterEmailSuffix()}
							</Label>
							<Input
								placeholder={userEmail}
								value={confirmEmail}
								onChange={(e) => setConfirmEmail(e.target.value)}
								disabled={isPending}
							/>
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setOpen(false)}
								disabled={isPending}
							>
								{m.common_cancel()}
							</Button>
							<Button
								variant="destructive"
								disabled={!isConfirmed || isPending}
								onClick={() => deleteAccount()}
							>
								{isPending ? m.danger_deleting() : m.danger_confirmDeleteTitle()}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</CardContent>
		</Card>
	);
}
